import React, { useMemo } from "react";
import { BaseSectionType, InteractionCounts } from "./types";
import { Box, Flex, Heading, Text, Badge, IconButton, Image, Button, Link } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import CategoryTag from "./CategoryTag";
import { useMeetingContext } from "./MeetingContext";
import { DownloadIcon } from "@chakra-ui/icons";
import { formatDate, formatTime } from "../../utils/timeUtils";
import AudioRecordingPlayer from "./AudioRecordingPlayer";



// Section name mapping - simplified for the current API-based implementation
const sectionNameMapping: Record<BaseSectionType, string> = {
  "Strategic Summary": "Strategic Summary",
  "Meeting Summary": "Meeting Summary",
  "Key Topics": "Key Topics",
  "Action Items": "Action Items",
  "Participants": "Participants",
  "Decisions Made": "Decisions Made",
  "Questions Raised": "Questions Raised",
  "Client's Snapshot": "Client's Snapshot",
  "Key Drivers & Risks": "Key Drivers & Risks",
  "Product Options": "Product Options",
  "Emotional Arc & Sentiment": "Emotional Arc & Sentiment",
  "All Interactions": "All Interactions"
};

// Define a type for our category items
type CategoryItem = {
  baseSection: BaseSectionType;
  display: string;
  subtext?: string;
  hasColon?: boolean;
};

interface MeetingHeaderDetailsProps {
  interactionCount: InteractionCounts | null;
  activeSection: BaseSectionType;
  availableSections: BaseSectionType[]; // New prop for dynamic visibility
  onSectionChange: (section: string) => void;
  onDownloadReport: (section: string) => void;
  conversationId?: string | null;

}


const MeetingHeaderDetails: React.FC<MeetingHeaderDetailsProps> = ({
  interactionCount,
  activeSection,
  availableSections,
  onSectionChange,
  onDownloadReport,
  conversationId
}) => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();


  // Get meeting data from context
  const { meetings } = useMeetingContext();
  const meeting = meetings?.find(m => m.meeting_id === meetingId) || null;

  // If meeting is not found, we'll show a placeholder
  const isMeetingFound = meeting !== null;

  // Generate categories in the correct order
  const categories: CategoryItem[] = useMemo(() => {
    const allCategories: CategoryItem[] = [
      {
        baseSection: "Strategic Summary",
        display: sectionNameMapping["Strategic Summary"]
      },
      {
        baseSection: "Meeting Summary",
        display: sectionNameMapping["Meeting Summary"]
      },
      {
        baseSection: "Key Topics",
        display: sectionNameMapping["Key Topics"]
      },
      {
        baseSection: "Action Items",
        display: sectionNameMapping["Action Items"]
      },
      {
        baseSection: "Participants",
        display: sectionNameMapping["Participants"]
      },
      {
        baseSection: "Decisions Made",
        display: sectionNameMapping["Decisions Made"]
      },
      {
        baseSection: "Questions Raised",
        display: sectionNameMapping["Questions Raised"]
      },
      {
        baseSection: "Client's Snapshot",
        display: sectionNameMapping["Client's Snapshot"]
      },
      {
        baseSection: "Key Drivers & Risks",
        display: sectionNameMapping["Key Drivers & Risks"]
      },
      {
        baseSection: "Product Options",
        display: sectionNameMapping["Product Options"]
      },
      {
        baseSection: "Emotional Arc & Sentiment",
        display: sectionNameMapping["Emotional Arc & Sentiment"]
      },
      {
        baseSection: "All Interactions",
        display: sectionNameMapping["All Interactions"],
        subtext: interactionCount ? `(${interactionCount.allInteractionsCount} Covered /${interactionCount.missedInteractionsCount} Missed)` : '',
      }
    ];

    // Filter categories based on availableSections
    return allCategories.filter(cat => availableSections.includes(cat.baseSection));
  }, [interactionCount, availableSections]);

  const handleCategoryClick = (baseSection: BaseSectionType) => {
    onSectionChange(baseSection);
  };
  const handleDownloadReport = async () => {
    try {
      await onDownloadReport(activeSection)
    } catch (error) {
      console.error('Error downloading report', error);
    }
  };

  const isCategoryActive = (baseSection: BaseSectionType) => {
    return baseSection === activeSection;
  };

  const handleClose = () => {
    navigate('/meetings');
  };

  return (
    <Box as="header" mb={8}>
      <Flex
        justify="space-between"
        align="center"
        mb={8}
      >
        <Box>
          <Flex align="center" gap={3}>
            <Heading
              as="h1"
              fontSize="2xl"
              fontWeight="bold"
              color="blue.900"
            >
              Meeting Details
            </Heading>
            {isMeetingFound ? (
              <Badge
                colorScheme="green"
                fontSize="xs"
                py={1}
                px={2}
                borderRadius="md"
              >
                FOUND
              </Badge>
            ) : (
              <Badge
                colorScheme="red"
                fontSize="xs"
                py={1}
                px={2}
                borderRadius="md"
              >
                NOT FOUND
              </Badge>
            )}
          </Flex>
          {isMeetingFound ? (
            <Text color="gray.500" fontSize="sm" mt={1}>
              {meeting.client} | {meeting.name}
            </Text>
          ) : (
            <Text color="gray.500" fontSize="sm" mt={1}>
              Client Meeting ID: {meetingId} | This meeting does not exist in the database.
            </Text>
          )}
        </Box>

        <IconButton
          aria-label="Close"
          icon={<Image
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/82ae17edeaa925b21e8e32fc9e23ac0045edfb9c?placeholderIfAbsent=true&apiKey=9181b4b5be8946e9a117f76f8f1082b9"
            objectFit="contain"
            w="19px"
            h="19px"
            alt="Close"
          />}
          variant="ghost"
          onClick={handleClose}
          _hover={{ bg: "gray.100" }}
        />
      </Flex>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "flex-start", md: "center" }}
        mb={4}
      >
        <Flex
          gap={3}
          flexWrap="wrap"
          ml={0}
          width={{ base: "100%", md: "auto" }}
          justify="flex-start"
          pb={{ base: 2, md: 0 }}
        >
          {categories.map((category, index) => {
            // Create proper category object for CategoryTag
            const categoryProp = category.subtext
              ? { text: category.display, subtext: category.subtext, hasColon: category.hasColon }
              : category.display;

            return (
              <Box
                key={index}
                onClick={() => handleCategoryClick(category.baseSection)}
                cursor="pointer"
                minWidth="fit-content"
              >
                <CategoryTag
                  category={categoryProp}
                  isActive={isCategoryActive(category.baseSection)}
                />
              </Box>
            );
          })}
        </Flex>
      </Flex>
      <Flex gap={2} align="center" fontSize="md" mb={{ base: 4, md: 0 }}>
        <Text color="black" fontWeight="medium">Date:</Text>
        <Text color="black">{isMeetingFound ? formatDate(meeting.date) || "Unknown" : "Unknown"}</Text>
        {/* <Text color="black" fontWeight="medium">| Time:</Text>
        <Text color="black">{isMeetingFound ? formatTime(meeting.date) || "Unknown" : "Unknown"}</Text> */}
        {/* {isMeetingFound && meeting.project && (
          <>
            <Text color="black" fontWeight="medium" ml={4}>Project:</Text>
            <Text color="black">{meeting.project}</Text>
          </>
        )} */}
        <Button
          // as="a"
          // href=""
          // download
          onClick={handleDownloadReport}
          leftIcon={<DownloadIcon />}
          variant="outline"
          border={'1px solid grey'}
          h={'30px'}
          w={'150px'}
          borderRadius={0}
          fontSize={'12px'}
          ml={14}
        >
          Download Report
        </Button>
        <AudioRecordingPlayer conversationId={conversationId} />
      </Flex>
    </Box>
  );
};

export default MeetingHeaderDetails; 